import { QueryClient } from '@tanstack/react-query'
import {
  BlogPostsVars,
  prefetchBlogPosts,
} from '@vangst/services/graphcms/marketing/useBlogPosts'
import { BlogPostFragment, Tags } from '@vangst/services/graphcms/types'
import { fetchPage } from '@vangst/services/oogst/page'
import { RobotsEnum } from '@vangst/services/oogst/types'
import Routes from '@vangst/services/routes'
import { InferGetStaticPropsType } from 'next'
import Image from 'next/image'
import Breadcrumbs from '../../components/navigation/Breadcrumbs'
import ClickyExplore from '../../components/navigation/ClickyExplore'
import ClickyLink from '../../components/navigation/ClickyLink'
import PageHeader from '../../components/views/PageHeader'
import Meta from '../../domains/application/Meta'
import BlogPostsResults from '../../domains/marketing/BlogPostsResults'
import {
  ResourceBlogSkittle,
  ResourceReportSkittle,
  ResourceRequirementsSkittle,
  Skittles,
} from '../../domains/marketing/Skittles'

export async function getStaticProps() {
  const queryClient = new QueryClient()
  const rawUrl = __filename.split('pages')[1]?.replace('.js', '')
  const { getPage } = await fetchPage(queryClient, { rawUrl })
  const page = { ...getPage }

  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: null, text: 'Resources' },
  ]

  const tagsPosts = {
    tags_contains_all: [],
    tags_contains_none: [Tags.Reports],
  } as BlogPostsVars

  const tagsReports = {
    tags_contains_all: [Tags.Reports],
    tags_contains_none: [],
  } as BlogPostsVars

  const posts = await prefetchBlogPosts(queryClient, {
    searchTerm: '',
    first: 3,
    ...tagsPosts,
  })

  const reports = await prefetchBlogPosts(queryClient, {
    searchTerm: '',
    first: 2,
    ...tagsReports,
  })

  return {
    props: {
      crumbs,
      page,
      posts: posts && posts?.length > 0 ? posts : [],
      reports: reports && reports?.length > 0 ? reports : [],
    },
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function LearnPage(props: PropsType) {
  const { crumbs, page, posts, reports } = props

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page.metaInfo?.canonicalUrl}
        crumbs={crumbs}
        description={page.metaInfo?.description}
        image={page.pagePhotos?.[0]?.url}
        publishDate={page.postedAt}
        robots={page.metaInfo?.robots ?? RobotsEnum.IndexFollow}
        title={page.metaInfo?.title ?? page.title ?? 'Vangst | Blog Posts'}
      />
      <Breadcrumbs crumbs={crumbs} />
      <PageHeader
        accent="text-blue"
        icon="psychology"
        title={
          page.title ??
          'Stay up to date on cannabis job and industry trends and learn from industry leaders.'
        }
        subtitle={page.subtitle}
        description={page.description}
        imgAlt={page.pagePhotos?.[0]?.alt}
        imgSrc={page.pagePhotos?.[0]?.url}
      />

      <Skittles>
        <ResourceReportSkittle />
        <ResourceRequirementsSkittle />
        <ResourceBlogSkittle />
      </Skittles>

      <section className="contain-xyc mood-white mt-8 grid gap-8">
        <div className="grid sm:flex sm:items-center sm:justify-between">
          <h2 className="heading-sm">Cannabis Job Reports and Guides</h2>

          <ClickyLink
            as="text-icon"
            href={Routes.REPORTS}
            className="link-orange mt-8"
          >
            View All Reports
          </ClickyLink>
        </div>
        <BlogPostsResults
          posts={reports as BlogPostFragment[]}
          className="grid gap-16 md:grid-cols-2"
        />
      </section>

      <section className="contain-xyc mood-white mt-8 grid gap-8">
        <h2 className="heading-sm">
          How to Work in Cannabis and Get a Cannabis Job
        </h2>
        <div className="relative h-128 w-full">
          <Image
            alt="Hands holding flower"
            className="h-auto max-w-full rounded-2xl object-cover"
            fill
            sizes="(min-width: 64em) 50vw, (min-width: 86em) 86em, 100vw"
            src="https://assets.vangst.com/marketing-site/2023/hands-holding-flower-large.png"
          />
        </div>
        <p>
          Politics, budgets, and public support vary widely depending on where
          you live. We've curated a list of how to get licensed and badged to
          help clarify what you need to know to get hired for a job near you.
        </p>
        <ClickyExplore
          href={Routes.REPORTS_STATE_REQUIREMENTS}
          className="mt-8"
        >
          Learn More
        </ClickyExplore>
      </section>

      <section className="contain-xyc mood-white mt-8 grid gap-8">
        <div className="grid sm:flex sm:items-center sm:justify-between">
          <h2 className="heading-sm">Latest Blog Posts</h2>

          <ClickyLink
            as="text-icon"
            href={Routes.BLOG_VANGST}
            className="link-orange mt-8"
          >
            View All Posts
          </ClickyLink>
        </div>
        <BlogPostsResults
          posts={posts as BlogPostFragment[]}
          className="grid gap-16 md:grid-cols-2 lg:grid-cols-3"
        />
      </section>
    </>
  )
}
